import { createHash } from 'node:crypto';
import type { RepoCard } from '@fim/futurex-protocol';
import { buildRepoCard, findRepoRoot, readMemory } from './repo.js';
import { buildSystemPrompt } from './system-prompt.js';
import { loadRetriever, type Retriever } from './index-store.js';

/** The per-session context: the repo, the pinned stable prefix, and the retriever. */
export interface SessionContext {
  readonly root: string;
  readonly card: RepoCard | null;
  /** The stable, cache-targeted system prefix (built once, never mutated). */
  readonly system: string;
  /**
   * Fingerprint of {@link system}, captured at session open. The prefix is pinned
   * for the life of the session so DeepSeek prefix-caching hits; comparing it to a
   * recompute from disk is the cache canary. See {@link currentPrefixDigest}.
   */
  readonly systemDigest: string;
  /** Loaded repo index for retrieval augmentation, or null if none/disabled. */
  readonly retriever: Retriever | null;
}

/** Short content fingerprint of the stable prefix (first 12 hex of SHA-256). */
export function prefixDigest(system: string): string {
  return createHash('sha256').update(system).digest('hex').slice(0, 12);
}

/**
 * Recompute the prefix digest from what is on disk RIGHT NOW (futurex-memory.md +
 * repo card). Compared against the pinned {@link SessionContext.systemDigest} this
 * is the cache canary: a difference means the source changed mid-session. The live
 * session deliberately keeps the pinned prefix (so DeepSeek's cache still hits) —
 * the caller just warns the user to restart to pick the change up.
 */
export function currentPrefixDigest(
  ctx: SessionContext,
  opts: { readonly useMemory: boolean; readonly useRepo: boolean },
): string {
  const card = opts.useRepo ? buildRepoCard(ctx.root) : null;
  const memory = opts.useMemory ? readMemory(ctx.root) : null;
  return prefixDigest(buildSystemPrompt(card, memory));
}

/** Detect the repo and assemble the stable prefix + retriever according to flags. */
export function loadContext(opts: {
  readonly useMemory: boolean;
  readonly useRepo: boolean;
  readonly useContext: boolean;
}): SessionContext {
  const root = findRepoRoot();
  const card = opts.useRepo ? buildRepoCard(root) : null;
  const memory = opts.useMemory ? readMemory(root) : null;
  const retriever = opts.useContext ? loadRetriever(root) : null;
  const system = buildSystemPrompt(card, memory);
  return { root, card, system, systemDigest: prefixDigest(system), retriever };
}
